import React, { useState, useEffect } from "react";
import { fetchUserAppAPI } from "../utils/api";
import CloseIcon from "@mui/icons-material/Close";
import loginlogo from "../assets/images/login-logo.png";
import "./LoginModal.css";

export default function LoginModal({ show, onClose, onSuccess }) {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1 = Enter Mobile, 2 = Enter OTP/Signup
  const [userExist, setUserExist] = useState(1);
  const [sentOtp, setSentOtp] = useState("");

  // Registration fields for new users
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Reset state whenever modal opens or closes
  useEffect(() => {
    if (!show) {
      setMobile("");
      setOtp("");
      setStep(1);
      setUserExist(1);
      setSentOtp("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setError("");
      setSuccessMsg("");
      setLoading(false);
    }
  }, [show]);

  if (!show) return null;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const response = await fetchUserAppAPI("login-otp", {
        method: "POST",
        body: JSON.stringify({ user_mobile: mobile }),
      });

      if (response && response.status === 200) {
        setSentOtp(response.result);
        setUserExist(response.user_exist);
        setStep(2);
        setSuccessMsg(response.msg || `OTP sent successfully.`);
      } else {
        setError(response.msg || "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndLogin = async (e) => {
    e.preventDefault();
    if (otp !== String(sentOtp)) {
      setError("Incorrect OTP. Please enter the valid OTP.");
      return;
    }

    if (userExist === 0 && (!firstName.trim() || !lastName.trim() || !email.trim())) {
      setError("Please fill in all registration fields.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const bodyPayload = {
        user_mobile: mobile,
      };

      if (userExist === 0) {
        bodyPayload.user_first_name = firstName;
        bodyPayload.user_last_name = lastName;
        bodyPayload.user_email = email;
        bodyPayload.user_device_token = "web_client_token";
      }

      const response = await fetchUserAppAPI("login", {
        method: "POST",
        body: JSON.stringify(bodyPayload),
      });

      if (response && response.status === 200 && response.result) {
        localStorage.setItem("user", JSON.stringify(response.result));
        setSuccessMsg("Logged in successfully!");
        
        // Broadcast login event to header & whole app
        window.dispatchEvent(new Event("userStateChange"));

        if (onSuccess) {
          onSuccess(response.result);
        }

        setTimeout(() => {
          onClose();
        }, 600);
      } else {
        setError(response.msg || "Login failed. Please try again.");
      }
    } catch (err) {
      setError("An error occurred during login. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal-close" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>

        <div className="login-modal-header text-center mb-3">
          <img src={loginlogo} alt="CareAtHome Logo" className="login-modal-logo mb-2" />
          <h4 className="fw-bold mb-1" style={{ color: "#13baac" }}>Log In / Sign Up</h4>
          <p className="text-muted small mb-0">
            {step === 1
              ? "Enter your Mobile number to receive OTP"
              : userExist === 0
              ? "Create your account to complete registration"
              : `Enter the OTP sent to +91-${mobile}`}
          </p>
        </div>

        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {successMsg && <div className="alert alert-success py-2 small">{successMsg}</div>}

        {step === 2 && sentOtp && (
          <div className="alert alert-warning py-1 small text-center">
            <strong>Developer OTP:</strong> {sentOtp}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleSendOtp}>
            <div className="form-group mb-3">
              <label className="form-label small fw-semibold text-muted">Mobile Number</label>
              <div className="input-group">
                <span className="input-group-text bg-light text-muted fw-semibold">+91</span>
                <input
                  type="tel"
                  className="form-control form-control-lg fs-6"
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 py-2 fw-semibold"
              style={{ backgroundColor: "#13baac", borderColor: "#13baac" }}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Get OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyAndLogin}>
            {userExist === 0 && (
              <>
                <div className="form-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group mb-2">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </>
            )}

            <div className="form-group mb-3">
              <label className="form-label small fw-semibold text-muted">Enter 4-Digit OTP</label>
              <input
                type="text"
                className="form-control form-control-lg text-center letter-spacing-2 fs-5"
                placeholder="• • • •"
                maxLength="4"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={() => {
                  setStep(1);
                  setError("");
                  setSuccessMsg("");
                }}
                disabled={loading}
              >
                Change Number
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-fill fw-semibold"
                style={{ backgroundColor: "#13baac", borderColor: "#13baac" }}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-3 text-center">
          <p className="text-muted" style={{ fontSize: "11px" }}>
            By continuing, you agree to CareAtHome's Terms of Service & Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
